import { createClasses, TransparentButton } from "@bloomberg/media-ui-core";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRegUIClient } from "@foundation/reg-ui-client";
import type {
  WatchlistCompareResponse,
  WatchlistComparison,
  WatchlistInfo,
} from "@foundation/watchlist-client";
import { Placeholder } from "@bloomberg/media-ui-article";
import type { TickerChartDataPoint } from "@bloomberg/media-ui-markets";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import styles from "./styles.module.css";
import WatchlistDashboardTitle from "~components/WatchlistDashboardTitle";
import {
  DASHBOARD_v2_FEEDBACK_URL,
  LOADING_STATE,
  NO_COMPARISON_TICKER,
} from "~constants/Portfolio";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import {
  fetchWatchlistCompare,
  updateWatchlistCompare,
} from "~api/portfolio/portfolioData";
import WatchlistComparisonChart from "~components/WatchlistComparisonChart";
import TickerChartLegend from "~components/TickerChartLegend";
import type { TickerChartLegendData } from "~types/types";
import {
  DEFAULT_COMPARISON_TICKERS,
  getChartTickerColor,
  normalizeComparison,
} from "~components/WatchlistComparisonChart/utils";
import WatchlistCompareSearch from "~components/WatchlistCompareSearch";
import BetaPopover from "~components/BetaPopover";
import ShareFeedback from "~components/ShareFeedback";

function getTickerChartLegends(
  portInfo: WatchlistInfo,
  comparisons: Array<WatchlistComparison>,
  tickerLegends: Array<TickerChartLegendData>
) {
  const unSelectedTickers = new Set<string>();

  // remember user selection of comparison view
  tickerLegends.forEach((tickerLegend) => {
    if (!tickerLegend.checked) {
      unSelectedTickers.add(tickerLegend.id.toString());
    }
  });

  let comparisonLegends;

  if (comparisons.length) {
    // remove invalid comparisons
    comparisonLegends = normalizeComparison(comparisons).map(
      (comparison, index) =>
        ({
          id:
            comparison.comparisonTicker ||
            comparison.comparisonWatchlistId?.toString(),
          name:
            comparison.comparisonTicker || comparison.comparisonWatchlistName,
          color: getChartTickerColor(index + 1),
          checked:
            // select new comparisons when added
            comparison.compareId === -1 ||
            // retain existing user selection
            !unSelectedTickers.has(
              (comparison.comparisonTicker ||
                comparison.comparisonWatchlistId?.toString()) as string
            ),
          disabled: false,
        } as TickerChartLegendData)
    );
  } else {
    comparisonLegends = DEFAULT_COMPARISON_TICKERS.map((ticker, index) => ({
      id: ticker,
      name: ticker,
      color: getChartTickerColor(index + 1),
      checked: !unSelectedTickers.has(ticker),
      disabled: false,
    }));
  }

  return [
    {
      id: portInfo.id.toString(),
      name: portInfo.name,
      color: getChartTickerColor(0),
      checked: true,
      disabled: true,
    },
    ...comparisonLegends,
  ];
}

function ComparisonLoadingState() {
  return (
    <div className="w-full">
      <Placeholder>
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
      </Placeholder>
    </div>
  );
}

export default function WatchlistFlexiCompare() {
  const { dashboardView, selectedPortfolio, updateDashboardView } =
    usePortfolioPage();

  const { session } = useRegUIClient();
  const bconnectId = session?.userInfo?.userId ?? "";

  const prevPortfolioId = useRef(selectedPortfolio?.portInfo?.id);

  const apiCalled = useRef(false);

  const [tickerLegends, setTickerLegends] = useState<
    Array<TickerChartLegendData>
  >([]);
  const [watchlistComparisons, setWatchlistComparisons] = useState<
    Array<WatchlistComparison>
  >([]);

  const [transientWatchlistComparisons, setTransientWatchlistComparisons] =
    useState<Array<WatchlistComparison>>([]);

  const [isMaximized, setIsMaximized] = useState(false);
  const [comparisonActionMessage, setComparisonActionMessage] = useState("");
  const [loadingState, setLoadingState] = useState(LOADING_STATE.EMPTY);

  const [hoveredChartData, setHoveredChartData] = useState<
    TickerChartDataPoint | undefined
  >(undefined);

  useEffect(() => {
    // update maximized state, when dashboard view is updated
    // to handle updates from external components
    const isComparisonChart = dashboardView === "COMP";

    setIsMaximized(isComparisonChart);
  }, [dashboardView]);

  const refreshWatchlistComparisons = () => {
    if (!selectedPortfolio?.portInfo?.id) {
      return;
    }

    // skip refresh when in-progress
    if (apiCalled.current) return;

    // set loading state
    apiCalled.current = true;
    setLoadingState(LOADING_STATE.LOADING);

    fetchWatchlistCompare({
      bconnectId,
      watchlistId: selectedPortfolio.portInfo.id,
    })
      .then((result) => {
        let comparisons: Array<WatchlistComparison> = (
          result.data as WatchlistCompareResponse
        ).watchlistComparisons;

        if (!comparisons?.length) {
          // set default comparisons
          comparisons = DEFAULT_COMPARISON_TICKERS.map((ticker) => ({
            comparisonWatchlistCurrency: "USD",
            comparisonTicker: ticker,
            compareId: -1,
            watchlistId: selectedPortfolio.portInfo.id,
          }));
        }

        setTickerLegends((prevState) => {
          return getTickerChartLegends(
            selectedPortfolio.portInfo,
            comparisons,
            prevState
          );
        });

        setWatchlistComparisons(comparisons);

        // reset api called status
        apiCalled.current = false;
        setLoadingState(LOADING_STATE.COMPLETE);
      })
      .catch((_) => {
        // reset api called status
        apiCalled.current = false;
        setLoadingState(LOADING_STATE.FAILED);
      });
  };

  // refresh watchlist comparison when watchlist selection changes
  useEffect(refreshWatchlistComparisons, [
    bconnectId,
    selectedPortfolio?.portInfo.id,
  ]);

  useEffect(() => {
    if (!selectedPortfolio?.portInfo) return;

    // Do not refresh chart on watchlist selection change
    if (selectedPortfolio?.portInfo?.id !== prevPortfolioId.current) {
      prevPortfolioId.current = selectedPortfolio?.portInfo?.id;

      return;
    }

    setTickerLegends((prevState) =>
      getTickerChartLegends(
        selectedPortfolio?.portInfo,
        watchlistComparisons,
        prevState
      )
    );
  }, [selectedPortfolio?.portInfo?.name]);

  const updateWatchlistComparisons = useCallback(
    (updatedWatchlistComparisons: WatchlistComparison[]) => {
      if (!selectedPortfolio?.portInfo) {
        return;
      }

      // set loading state, to avoid further updates and notify user
      setLoadingState(LOADING_STATE.LOADING);

      // store updated copy for retry actions
      setTransientWatchlistComparisons(updatedWatchlistComparisons);

      // update database to remove the ticker/watchlist
      updateWatchlistCompare({
        bconnectId,
        watchlistId: selectedPortfolio.portInfo.id,
        watchlistCompareData: {
          watchlistComparisons: updatedWatchlistComparisons,
        },
      })
        .then((result) => {
          const resultData = result.data as WatchlistCompareResponse;

          // fetch comparisons from db, when result data is not available
          if (!resultData) {
            refreshWatchlistComparisons();

            return;
          }

          setTickerLegends((prevState) => {
            return getTickerChartLegends(
              selectedPortfolio?.portInfo,
              resultData.watchlistComparisons,
              prevState
            );
          });

          // update watchlist comparisons
          setWatchlistComparisons(resultData.watchlistComparisons);

          // clear updated copy
          setTransientWatchlistComparisons([]);
          setLoadingState(LOADING_STATE.COMPLETE);
          setComparisonActionMessage("Your changes have been saved.");
        })
        .catch((_) => {
          setLoadingState(LOADING_STATE.FAILED);
          setComparisonActionMessage("Your changes could not be saved.");
        });
    },
    [bconnectId, selectedPortfolio?.portInfo.id]
  );

  const handleTickerSelectionChange = useCallback(
    (tickerId: string, isChecked: boolean) => {
      const isNumeric = (ticker: string) => /^[+-]?\d+(\.\d+)?$/.test(ticker);

      const comparisonType = isNumeric(tickerId) ? "watchlist-" : "";
      const event = isChecked ? "quote_add_compare" : "quote_remove_compare";
      let eventLabel;

      if (isChecked && isMaximized) {
        eventLabel = `add-compare-${comparisonType}max-view`;
      } else if (isChecked && !isMaximized) {
        eventLabel = `add-compare-${comparisonType}min-view`;
      } else if (!isChecked && isMaximized) {
        eventLabel = `remove-compare-${comparisonType}max-view`;
      } else {
        eventLabel = `remove-compare-${comparisonType}min-view`;
      }

      trackClickEvent(event, eventLabel, "click", "Market Data");

      // update selection state for ticker legend
      setTickerLegends((prevState) => {
        return prevState.map((tickerLegend) => {
          if (tickerLegend.id === tickerId) {
            return {
              ...tickerLegend,
              checked: isChecked,
            };
          }

          return tickerLegend;
        });
      });
    },
    [setTickerLegends, isMaximized]
  );

  const handleTickerRemoval = useCallback(
    (itemId: string) => {
      const updatedWatchlistComparisons = watchlistComparisons.filter(
        (comparison) =>
          comparison.comparisonTicker !== itemId &&
          comparison.comparisonWatchlistId !== Number(itemId)
      );

      const removedComparison = watchlistComparisons.find((comparison) => {
        return (
          comparison.comparisonTicker === itemId ||
          comparison.comparisonWatchlistId?.toString() === itemId
        );
      });

      if (removedComparison?.comparisonTicker) {
        trackClickEvent(
          "quote_remove_compare",
          "remove-compare-from-db",
          "click",
          "Market Data"
        );
        trackClickEvent(
          "quote_remove_compared_security",
          itemId,
          "click",
          "Market Data"
        );
      } else if (removedComparison?.comparisonWatchlistId) {
        trackClickEvent(
          "quote_remove_compare",
          "remove-compare-watchlist-from-db",
          "click",
          "Market Data"
        );
      }

      // when all comparisons are removed, add a no-compare entry
      // to handle user's preference to view chart any comparison
      if (!updatedWatchlistComparisons.length) {
        const newComparison: WatchlistComparison = {
          compareId: -1,
          watchlistId: selectedPortfolio?.portInfo.id as number,
          comparisonTicker: NO_COMPARISON_TICKER,
        };

        // add no-compare as an option, will be ignored by
        // component while rendering comparisons
        updatedWatchlistComparisons.push(newComparison);
      }

      // update db to remove the ticker removed
      updateWatchlistComparisons(updatedWatchlistComparisons);
    },
    [
      updateWatchlistComparisons,
      watchlistComparisons,
      selectedPortfolio?.portInfo.id,
    ]
  );

  // handle user comparison selection
  const onCompareSelection = useCallback(
    (
      comparisonType: "ticker" | "watchlist",
      value: string,
      displayName?: string
    ) => {
      // skip when the comparison type is invalid
      if (comparisonType !== "watchlist" && comparisonType !== "ticker") {
        return;
      }

      let selectedComparison;

      // find if the selected value exists
      if (comparisonType === "ticker") {
        selectedComparison = watchlistComparisons.find(
          (comparison) =>
            comparison.comparisonTicker?.toUpperCase() === value.toUpperCase()
        );
      } else {
        selectedComparison = watchlistComparisons.find(
          (comparison) => comparison.comparisonWatchlistId?.toString() === value
        );
      }

      // skip when update is not required
      if (selectedComparison) {
        return;
      }

      const newComparison: WatchlistComparison = {
        compareId: -1,
        watchlistId: selectedPortfolio?.portInfo.id as number,
      };

      if (comparisonType === "ticker") {
        newComparison.comparisonTicker = value;
      } else {
        newComparison.comparisonWatchlistId = Number(value);
        newComparison.comparisonWatchlistName = displayName;
      }

      // remove the no-comparison value when new comparisons are added
      const validWatchlistComparison = watchlistComparisons.filter(
        (comparison) => comparison.comparisonTicker !== NO_COMPARISON_TICKER
      );

      // update db to add the ticker/watchlist added
      updateWatchlistComparisons([...validWatchlistComparison, newComparison]);
    },
    [
      selectedPortfolio?.portInfo.id,
      updateWatchlistComparisons,
      watchlistComparisons,
    ]
  );

  const toggleView = useCallback(() => {
    // reset dashboard view
    const nextDashboardView = isMaximized ? "DASH" : "COMP";

    const clickEvent = isMaximized ? "watchlistHide" : "watchlistShow";
    const eventLabel = isMaximized
      ? "minimize_view_comparison_chart"
      : "expand_view_comparison_chart";

    trackClickEvent(clickEvent, eventLabel, "click", "watchlist");

    setIsMaximized(!isMaximized);
    updateDashboardView(nextDashboardView);

    // reset comparison action message
    setComparisonActionMessage("");
  }, [isMaximized, updateDashboardView]);

  const onSaveClick = useCallback(() => {
    updateWatchlistComparisons(transientWatchlistComparisons);
  }, [transientWatchlistComparisons, updateWatchlistComparisons]);

  const comparisonsRemaining = 5 - tickerLegends.length;
  let comparisonRemainingMessage = "You've reached comparison limit";

  if (comparisonsRemaining === 1) {
    comparisonRemainingMessage = "You can add 1 more comparison";
  } else if (comparisonsRemaining > 1) {
    comparisonRemainingMessage = `You can add ${comparisonsRemaining} more comparisons`;
  }

  const cx = createClasses([
    styles.flexiCompareContainer,
    {
      [styles.maximized]: isMaximized,
    },
  ]);

  return (
    <div className={cx}>
      <BetaPopover
        className={"w-full"}
        eyebrow="Now Improved!"
        text={
          <span>
            You asked, we listened. This chart now includes expanded comparison
            features and considers lots.
            <br />
            <br />
            Have feedback?{" "}
            <ShareFeedback
              feedbackUrl={DASHBOARD_v2_FEEDBACK_URL}
              trackingEventLabel={"survey_comparison_chart"}
            />
          </span>
        }
        allowedPlacements={["left-start", "top-start"]}
        localStorageKey={"wl_flexi_compare_beta"}
      >
        <WatchlistDashboardTitle
          title="Comparison Chart"
          iconVariant="beta"
          description="Compare your watchlist’s performance against market benchmarks. Performance is calculated based on both price movement and lot allocations."
          feedbackUrl={DASHBOARD_v2_FEEDBACK_URL}
          feedbackTrackingEventLabel="survey_comparison_chart"
          tooltipOpenEventLabel="tooltip_comparison_chart_open"
          tooltipCloseEventLabel="tooltip_comparison_chart_close"
        >
          <TransparentButton
            size="small"
            className={styles.seeMoreLessActionButton}
            onClick={toggleView}
          >
            {isMaximized ? "See Less" : "See More"}
          </TransparentButton>
        </WatchlistDashboardTitle>
      </BetaPopover>

      <div className={styles.chartWrapper}>
        {/* Comparison Chart Section */}
        <WatchlistComparisonChart
          handleChartInteraction={setHoveredChartData}
          tickerChartLegends={tickerLegends}
          watchlistComparisons={watchlistComparisons}
          isMaximized={isMaximized}
        >
          {/* Ticker Legend for minimized state */}
          <TickerChartLegend
            className={styles.tickerChartLegend}
            hoveredDataPoint={hoveredChartData}
            tickerLegendData={tickerLegends}
            handleTickerSelectionChange={handleTickerSelectionChange}
          />
        </WatchlistComparisonChart>

        {/* Comparison Configuration Section */}
        <div className={styles.compareConfigContainer}>
          {loadingState === LOADING_STATE.LOADING ? (
            <ComparisonLoadingState />
          ) : (
            <>
              <div className={styles.tickerChartLegendContainer}>
                {/* Ticker Legend for maximized state */}
                <TickerChartLegend
                  className={styles.interactiveTickerChartLegend}
                  hoveredDataPoint={hoveredChartData}
                  tickerLegendData={tickerLegends}
                  enableTickerRemoval
                  applyInteractiveStyles
                  handleTickerSelectionChange={handleTickerSelectionChange}
                  handleTickerRemoval={handleTickerRemoval}
                />

                <WatchlistCompareSearch
                  comparisons={watchlistComparisons}
                  onCompareSelection={onCompareSelection}
                />

                <div className={styles.actionContainer}>
                  <span className={styles.actionMessage}>
                    {comparisonRemainingMessage}
                  </span>
                </div>
              </div>

              <div className={styles.actionContainer}>
                <span className={styles.actionMessage}>
                  {comparisonActionMessage}
                </span>
                {watchlistComparisons.length > 0 &&
                  loadingState === LOADING_STATE.FAILED && (
                    <TransparentButton
                      size="small"
                      onClick={onSaveClick}
                      type="submit"
                      version="phoenix"
                    >
                      Retry Now
                    </TransparentButton>
                  )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
